import type { EditorHintRegistry } from "./types";

/**
 * Registr nápověd editoru průvodce (řez 10, typy v `./types`). Bez runtime importů: měřidlo
 * `scripts/kontrola/admin-napovedy.mjs` ho načítá přes `nactiTs`.
 *
 * Editor nemá záložky (`tabs: {}`), jediný odkaz „Zobrazit na webu“ je v hlavičce. Osobní
 * kontakt průvodce (`email`, `phone`, `whatsapp`) web neukazuje (Ne-cíl vize) a žádný záznam
 * druhu `web` ho nekryje (`guide.test.ts`); hodnocení ani jazyky nápověda neslibuje.
 *
 * Domovská destinace (`homeDestinationId`, čte ji harmonogram) nemá políčko ani záznam: je to
 * zrcadlo první země, které při uložení `countryIds` počítá server, proto ji kryje `columns`
 * záznamu `countryIds`. Galerie průvodce nemá hlavní fotku (`primary`), jen portrét
 * (`photoMediaId`).
 */
const GD = "apps/web/src/components/pages/GuideDetail.astro";
const GC = "apps/web/src/components/GuideCard.astro";
const TD = "apps/web/src/components/pages/TripDetail.astro";

export const guideHintRegistry: EditorHintRegistry = {
  editor: "guide",
  table: "guides",
  variants: ["guide"],
  pages: { guide: "guide" },
  fields: {
    name: { kind: "web", hintKey: "webHints.guide.name", readers: [GD, GC] },
    slug: { kind: "web", hintKey: "webHints.guide.slug", readers: [GC, GD] },
    yearsExperience: {
      kind: "web",
      hintKey: "webHints.guide.yearsExperience",
      readers: [GD, GC, TD],
    },
    livingIn: { kind: "web", hintKey: "webHints.guide.livingIn", readers: [GD, GC, TD] },
    bio: { kind: "web", hintKey: "webHints.guide.bio", readers: [GD, TD] },
    specializations: {
      kind: "web",
      hintKey: "webHints.guide.specializations",
      readers: [GD, GC],
    },
    photoMediaId: {
      kind: "web",
      hintKey: "webHints.guide.photoMediaId",
      readers: [GD, GC, TD],
      readAs: ["photoMedia"],
    },
    countryIds: {
      kind: "web",
      hintKey: "webHints.guide.countryIds",
      readers: [GD, GC, "apps/web/src/lib/guideStrip.ts"],
      readAs: ["countries"],
      columns: ["homeDestinationId"],
    },
    interview: { kind: "web", hintKey: "webHints.guide.interview", readers: [GD] },
    tips: { kind: "web", hintKey: "webHints.guide.tips", readers: [GD] },
    galleryImageIds: {
      kind: "web",
      hintKey: "webHints.guide.galleryImageIds",
      readers: [GD],
      readAs: ["galleryMedia"],
    },
    // Veřejný výpis `/pruvodci` řadí abecedně (`compareCzech`); pořadí řídí jen harmonogram.
    sortOrder: {
      kind: "backoffice",
      hintKey: "webHints.guide.sortOrder",
      readers: ["apps/api/src/lib/scheduleWindow.ts"],
    },
    // Brána publikace: veřejné API vrací jen aktivní průvodce, web příznak sám nečte
    // (výjimka `ctenar` v `scripts/kontrola/admin-napovedy-vyjimky/guide.json`).
    isActive: {
      kind: "web",
      hintKey: "webHints.guide.isActive",
      readers: ["apps/api/src/routes/public/guides.ts"],
    },
  },
  columnsOutsideEditor: {
    id: { status: "system", reason: "technický sloupec" },
    createdAt: { status: "system", reason: "technický sloupec" },
    updatedAt: { status: "system", reason: "technický sloupec" },
    photo: {
      status: "system",
      reason:
        "stará adresa fotky z doby před knihovnou médií; web ji čte jako zálohu, nová fotka jde přes Fotografii",
    },
    bioSk: {
      status: "bezPolicka",
      reason:
        "vydává ho veřejná projekce průvodce (guidePublic.ts), web ho nevykresluje; políčko v editoru nikdy nebylo",
    },
    youtubeChannelUrl: {
      status: "bezPolicka",
      reason:
        "vydává ho veřejná projekce průvodce (guidePublic.ts), web ho nevykresluje; políčko v editoru nikdy nebylo",
    },
    featuredVideos: {
      status: "bezPolicka",
      reason:
        "vydává je veřejná projekce průvodce (guidePublic.ts), web je nevykresluje; políčko v editoru nikdy nebylo",
    },
    homeLocationId: {
      status: "bezPolicka",
      reason:
        "výchozí místo průvodce v harmonogramu a náhradách (scheduleWindow.ts, guideReplacement.ts); políčko chybí",
    },
    email: {
      status: "bezPolicka",
      reason:
        "kontakt pro back office (admin API odjezdů); web ho neukazuje (Ne-cíl vize); políčko chybí",
    },
    phone: {
      status: "bezPolicka",
      reason:
        "kontakt pro back office (admin API odjezdů); web ho neukazuje (Ne-cíl vize); políčko chybí",
    },
    whatsapp: {
      status: "necte",
      reason: "nic ho nečte; web ho ukázat nesmí (Ne-cíl vize)",
    },
  },
  tabs: {},
};
